import React, { useState, useCallback } from 'react';
import {
    IconButton, Popover, Box, Typography, Stack, Chip, Tooltip, alpha, useTheme, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { ModelConfig, ModelType, getModelVendor } from '@lib/models/enums';
import { useAiChatServices } from '../AiChatContext';

type ModelConfigEntry = (typeof ModelConfig)[keyof typeof ModelConfig];

interface ModelOption {
    id: string;
    config: ModelConfigEntry;
    vendor: string;
}

const AVAILABLE_MODELS: ModelOption[] = Object.entries(ModelConfig).map(([id, config]) => ({
    id,
    config,
    vendor: getModelVendor(id),
}));

const speedColor: Record<string, 'success' | 'info' | 'warning'> = {
    Fastest: 'success',
    Fast: 'success',
    Good: 'info',
    Moderate: 'warning',
};

const qualityColor: Record<string, 'secondary' | 'info' | 'warning'> = {
    Highest: 'secondary',
    High: 'info',
    Good: 'warning',
};

interface ModelSelectorProps {
    /**
     * Render the model list inline (no IconButton trigger, no popover)
     * — used by surfaces that already host their own settings menu and
     * want the model list embedded directly. Defaults to `false`.
     */
    inline?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ inline = false }) => {
    const theme = useTheme();
    const { currentModel, setCurrentModel } = useAiChatServices();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleSelect = useCallback((modelId: string) => {
        setCurrentModel(modelId);
        handleClose();
    }, [setCurrentModel, handleClose]);

    const currentConfig = currentModel ? (ModelConfig as Record<string, ModelConfigEntry>)[currentModel] : undefined;
    const CurrentIcon = currentConfig?.icon ?? SmartToyOutlinedIcon;

    const listContent = (
                    <Stack direction="row">
                        <Stack spacing={0.5} sx={inline ? { maxWidth: 250, flex: '0 0 250px', minWidth: 0 } : undefined}>
                            <Typography variant='h6' width={"100%"} textAlign={"center"}>Fast models</Typography>
                            <Divider />
                            {AVAILABLE_MODELS.map(({ id, config, vendor }) => {
                                const isSelected = id === currentModel;
                                const Icon = config.icon;

                                if (config.speed !== 'Fast') return null;

                                return (
                                    <Box
                                        key={id}
                                        onClick={() => handleSelect(id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                            p: 1.2,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor: isSelected ? 'primary.main' : 'transparent',
                                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                bgcolor: isSelected
                                                    ? alpha(theme.palette.primary.main, 0.12)
                                                    : 'action.hover',
                                            },
                                        }}
                                    >
                                        <Box sx={{ pt: 0.3 }}>
                                            <Icon fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }} />
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0,minHeight: '120px' }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {config.name}
                                                </Typography>
                                                {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
                                            </Stack>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
                                                {id}
                                            </Typography>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.3, display: 'block', whiteSpace: 'pre-line' }}>
                                                {config.description}
                                            </Typography>

                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.8 }}>
                                                <Chip
                                                    label={config.speed}
                                                    size="small"
                                                    color={speedColor[config.speed] ?? 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
                                                />
                                                <Chip
                                                    label={config.quality}
                                                    size="small"
                                                    color={qualityColor[config.quality] ?? 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
                                                />
                                                <Chip
                                                    label={vendor}
                                                    size="small"
                                                    variant="filled"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 }, bgcolor: 'action.selected' }}
                                                />
                                            </Stack>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                        <Stack spacing={0.5} sx={inline ? { maxWidth: 250, flex: '0 0 250px', minWidth: 0 } : undefined}>
                            <Typography variant='h6' width={"100%"} textAlign={"center"}>High Precision</Typography>
                            <Divider />
                            {AVAILABLE_MODELS.map(({ id, config, vendor }) => {
                                const isSelected = id === currentModel;
                                const Icon = config.icon;

                                if (config.quality !== 'Highest') return null;

                                return (
                                    <Box
                                        key={id}
                                        onClick={() => handleSelect(id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                            p: 1.2,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor: isSelected ? 'primary.main' : 'transparent',
                                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                bgcolor: isSelected
                                                    ? alpha(theme.palette.primary.main, 0.12)
                                                    : 'action.hover',
                                            },
                                        }}
                                    >
                                        <Box sx={{ pt: 0.3 }}>
                                            <Icon fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }} />
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0, minHeight: '120px', }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {config.name}
                                                </Typography>
                                                {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
                                            </Stack>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
                                                {id}
                                            </Typography>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.3, display: 'block', whiteSpace: 'pre-line' }}>
                                                {config.description}
                                            </Typography>

                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.8 }}>
                                                <Chip
                                                    label={config.speed}
                                                    size="small"
                                                    color={speedColor[config.speed] ?? 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
                                                />
                                                <Chip
                                                    label={config.quality}
                                                    size="small"
                                                    color={qualityColor[config.quality] ?? 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
                                                />
                                                <Chip
                                                    label={vendor}
                                                    size="small"
                                                    variant="filled"
                                                    sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 }, bgcolor: 'action.selected' }}
                                                />
                                            </Stack>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Stack>
    );

    if (inline) {
        return listContent;
    }

    return (
        <>
            <Tooltip title={currentConfig?.name ?? 'Select model'} placement="top">
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                        p: 1,
                    }}
                >
                    <CurrentIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ mt: -1 }}
            >
                <Box sx={{ p: 2, minWidth: 340, maxWidth: 500, bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                        Select Model
                    </Typography>

                    {listContent}
                </Box>
            </Popover>
        </>
    );
};

export default ModelSelector;
